import React, { useState, useRef } from 'react';
import {
    Box,
    Heading,
    Text,
    Button,
    Stack,
    useToast,
    Center,
    Flex,
    CloseButton,
    useColorModeValue,
    Icon,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalCloseButton,
    ModalFooter
} from '@chakra-ui/react';
import { FiUpload, FiFile, FiEye } from 'react-icons/fi';
import * as XLSX from 'xlsx';
import { importMissions } from './ImportMissionService'; // Import du service

const ImportMissionsPage: React.FC = () => {
    const [file, setFile] = useState<File | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [previewData, setPreviewData] = useState<any[]>([]);
    const [headers, setHeaders] = useState<string[]>([]);
    const toast = useToast();
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleFileSelected = (selected: File | null) => {
        if (
            selected &&
            (selected.name.endsWith('.xlsx') ||
                selected.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            )) {
            setFile(selected);
            setPreviewData([]);
            setHeaders([]);
        } else {
            toast({
                title: 'Format invalide',
                description: 'Utilisez un fichier .xlsx',
                status: 'error',
                duration: 4000
            });
        }
    };

    const handlePreview = async () => {
        if (!file) {
            toast({
                title: 'Aucun fichier',
                description: 'Veuillez sélectionner un fichier.',
                status: 'error',
                duration: 4000
            });
            return;
        }

        try {
            const data = await file.arrayBuffer();
            const workbook = XLSX.read(data, { type: 'array' });
            const firstSheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[firstSheetName];

            // Convertir en JSON
            const jsonData: any[] = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

            // Extraire les en-têtes (première ligne)
            const firstRow = jsonData[0] || [];
            setHeaders(firstRow.map(String));

            // Extraire les données (5 premières lignes après l'en-tête)
            const previewRows = jsonData.slice(1, 200).filter(row => row.length > 0);
            setPreviewData(previewRows);

            setIsPreviewOpen(true);
        } catch (error) {
            toast({
                title: 'Erreur de lecture',
                description: 'Impossible de lire le fichier Excel',
                status: 'error',
                duration: 4000
            });
        }
    };

    const handleDrag = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
    };
    const handleDragEnter = (e: React.DragEvent) => {
        handleDrag(e);
        setIsDragging(true);
    };
    const handleDragLeave = (e: React.DragEvent) => {
        handleDrag(e);
        setIsDragging(false);
    };
    const handleDrop = (e: React.DragEvent) => {
        handleDrag(e);
        setIsDragging(false);
        handleFileSelected(e.dataTransfer.files[0] ?? null);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!file)
            return toast({
                title: 'Aucun fichier',
                description: 'Veuillez sélectionner un fichier.',
                status: 'error',
                duration: 4000
            });

        setIsSubmitting(true);
        try {
            // Appel au service d'importation
            const resp = await importMissions(file);

            toast({
                title: resp.message,
                description: `missions importées.`,
                status: 'success',
                duration: 4000
            });

            setFile(null);
            setPreviewData([]);
            setHeaders([]);
        } catch (err: any) {
            let errorMessage = "Erreur lors de l'importation";

            if (err.response) {
                // Erreur avec réponse du serveur
                errorMessage = err.response.data?.message || err.message;
            } else if (err.request) {
                // Erreur de requête sans réponse
                errorMessage = "Aucune réponse du serveur";
            } else {
                // Autres erreurs
                errorMessage = err.message;
            }

            toast({
                title: 'Erreur',
                description: errorMessage,
                status: 'error',
                duration: 4000
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const dropBg = useColorModeValue('gray.50', 'gray.700');
    const dragBg = useColorModeValue('purple.50', 'purple.700');

    return (
        <Center minH="100vh" bg={useColorModeValue('purple.50','gray.800')} p={4}>
            <Box bg={useColorModeValue('white','gray.900')} rounded="xl" shadow="lg" w="full" maxW="2xl" p={8}>
                <Stack spacing={6} textAlign="center">
                    <Heading size="2xl" color="purple.700">Importation des Missions</Heading>
                    <Text color="gray.500">Téléversez votre fichier Excel pour mettre à jour la base.</Text>

                    <Box
                        borderWidth={2}
                        borderStyle="dashed"
                        rounded="lg"
                        p={10}
                        bg={isDragging ? dragBg : dropBg}
                        onDragEnter={handleDragEnter}
                        onDragOver={handleDragEnter}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        cursor="pointer"
                        onClick={() => fileInputRef.current?.click()}
                    >
                        <input
                            type="file"
                            ref={fileInputRef}
                            hidden
                            accept=".xlsx"
                            onChange={e => handleFileSelected(e.target.files?.[0] ?? null)}
                        />
                        <Flex direction="column" align="center">
                            <Icon as={FiUpload} boxSize={12} color="gray.400" />
                            <Text mt={2} fontWeight="medium">Glissez-déposez ou cliquez</Text>
                            <Text fontSize="sm" color="gray.400">.xlsx uniquement</Text>
                        </Flex>
                    </Box>

                    <Button
                        colorScheme="purple"
                        size="lg"
                        onClick={() => fileInputRef.current?.click()}
                    >
                        Sélectionner un fichier
                    </Button>

                    {file && (
                        <Stack spacing={4}>
                            <Stack
                                direction={{ base: "column", sm: "row" }}
                                align="center"
                                justify="space-between"
                                bg="purple.50"
                                p={4}
                                rounded="md"
                            >
                                <Flex align="center">
                                    <Icon as={FiFile} boxSize={8} color="purple.600" />
                                    <Text ml={4} isTruncated maxW={{ base: "200px", sm: "300px" }}>{file.name}</Text>
                                </Flex>

                                <Flex mt={{ base: 3, sm: 0 }} align="center">
                                    <Button
                                        leftIcon={<FiEye />}
                                        colorScheme="purple"
                                        onClick={handlePreview}
                                        isDisabled={isSubmitting}
                                        size="sm"
                                        mr={2}
                                    >
                                        Prévisualiser
                                    </Button>
                                    <CloseButton
                                        onClick={() => {
                                            setFile(null);
                                            setPreviewData([]);
                                        }}
                                    />
                                </Flex>
                            </Stack>
                        </Stack>
                    )}

                    <Button
                        colorScheme="purple"
                        isLoading={isSubmitting}
                        loadingText="Import en cours..."
                        onClick={handleSubmit}
                        isDisabled={!file}
                    >
                        Importer les Missions
                    </Button>
                </Stack>
            </Box>

            {/* Modal de prévisualisation */}
            <Modal
                isOpen={isPreviewOpen}
                onClose={() => setIsPreviewOpen(false)}
                size="full"
                scrollBehavior="inside"
            >
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Prévisualisation du fichier</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody overflowX="auto">
                        {previewData.length > 0 ? (
                            <Table variant="striped" size="sm">
                                <Thead bg="purple.500">
                                    <Tr>
                                        {headers.map((header, index) => (
                                            <Th key={index} color="white">{header}</Th>
                                        ))}
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {previewData.map((row, rowIndex) => (
                                        <Tr key={rowIndex}>
                                            {row.map((cell: any, cellIndex: number) => (
                                                <Td key={cellIndex} maxW="300px" overflow="hidden" textOverflow="ellipsis">
                                                    {cell}
                                                </Td>
                                            ))}
                                        </Tr>
                                    ))}
                                </Tbody>
                            </Table>
                        ) : (
                            <Center h="200px">
                                <Text fontSize="lg">Aucune donnée à afficher</Text>
                            </Center>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            colorScheme="blue"
                            onClick={() => setIsPreviewOpen(false)}
                        >
                            Fermer
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Center>
    );
};

export default ImportMissionsPage;